import React from 'react';
import { Autocomplete, Avatar, Box, Chip, FormLabel, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import moment from 'moment';
import dayjs from 'dayjs';
import ccs from 'countrycitystatejson';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
  bgcolor: selected ? color : 'transparent',
  color: selected ? '#fff' : color,
  border: `1px solid ${color}`,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const FinalDetailsCard = (props) => {
  const empStatus = props.empStatus.map((empSt) => ({ value: empSt, title: empSt }));
  const frequency = props.frequency.map((freq) => ({ value: freq, title: freq }));
  const empList = props.empList.map((emp) => ({ value: emp.id, title: emp.employee_name }));
  const code = ccs.getCountryByShort('US');
  const sub = props.submitAttempted;

  return (
    <Stack spacing={2}>
      {props.selectedEmp.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          Add at least one employee to the project
        </Typography>
      )}

      {props.selectedEmp.map((emp, indx) => (
        <Paper key={emp.empId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#e0e7ef' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: '#EBF8FF', color: '#1f3a5f', fontSize: '0.8rem' }}>{indx + 1}</Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{emp.empName}</Typography>
            </Box>
            {(+props.pManagerId !== emp.empId) && (
              <IconButton size="small" aria-label="remove employee" onClick={() => props.onDelete(emp.empId)} sx={{ color: '#d32f2f' }}>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Stack spacing={1.5}>
            <Autocomplete
              id="pEmpStatus"
              fullWidth
              size="small"
              value={empStatus.find((type) => type.title === emp.pEmpStatus) || null}
              onChange={(e, newValue) => props.handleChangeEmp(emp.empId, { target: { id: 'pEmpStatus', value: newValue ? newValue.title : '' } })}
              options={empStatus}
              getOptionLabel={(option) => option.title}
              renderInput={(params) => <TextField {...params} label="Employee Status" variant="outlined" required error={sub && !emp.pEmpStatus} />}
            />

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                sx={{ flex: 1 }}
                size="small"
                variant="outlined"
                label="Bill Rate"
                value={emp.pEmpBillRate}
                onChange={(e) => props.handleChangeEmp(emp.empId, e)}
                id="pEmpBillRate"
                type="number"
                required
                error={sub && !emp.pEmpBillRate}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                sx={{ flex: 1 }}
                size="small"
                variant="outlined"
                label="Currency"
                value={`${code.emoji} ${code.name}(${code.currency})`}
                id="pEmpCurrency"
                disabled
              />
            </Box>

            <Box>
              <FormLabel component="legend" required error={sub && !emp.pEmpFrequency} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
                Frequency
              </FormLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {frequency.length === 0 ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No frequencies available</Typography>
                ) : (
                  frequency.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.title}
                      onClick={() => props.handleChangeEmp(emp.empId, { target: { id: 'pEmpFrequency', value: opt.value } })}
                      sx={selectableChipSx(emp.pEmpFrequency === opt.value)}
                    />
                  ))
                )}
              </Box>
              {sub && !emp.pEmpFrequency && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>Frequency is required</Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Associate From"
                  value={moment(emp.pEmpAssFromDate).isValid() ? new Date(moment(emp.pEmpAssFromDate)) : null}
                  onChange={(d) => props.handleChangeEmp(emp.empId, { target: { value: dayjs(d).format('YYYY-MM-DDTHH:mm:ss'), id: 'pEmpAssFromDate' } })}
                  slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Associate To"
                  value={moment(emp.pEmpAssToDate).isValid() ? new Date(moment(emp.pEmpAssToDate)) : null}
                  onChange={(d) => props.handleChangeEmp(emp.empId, { target: { value: dayjs(d).format('YYYY-MM-DDTHH:mm:ss'), id: 'pEmpAssToDate' } })}
                  slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Stack>
        </Paper>
      ))}

      <Autocomplete
        id="pEmp"
        fullWidth
        size="small"
        value={null}
        onChange={(e, newValue) => {
          const value = newValue ? empList.find((emp) => emp.title === newValue.title) : null;
          if (value) props.handleSelectedEmp({ target: { value: value.value, id: 'pEmp' } });
        }}
        options={empList}
        getOptionLabel={(option) => option.title}
        renderInput={(params) => <TextField {...params} label="Add Employee" variant="outlined" />}
      />
    </Stack>
  );
};

export default FinalDetailsCard;
